import {
  ApiBranch,
  ApiContact,
  ApiCustomer,
  ApiCustomerAddress,
  ApiCustomerData,
  ApiCustomerDataNote,
  ApiCustomerDataParams,
  ApiCustomerOrderFilter,
  ApiGetDataParam,
  ApiGetListParams,
} from "./interface.api";
import {
  ApiFileDownloadRequestID,
  ApiHooksProps
} from "../interface";
import { Customer, CustomerData, Order, OrderNote } from "@/types";
import {
  CustomerApi,
  GetListParams
} from "./interface";
import {
  customerApiDataNotesMapper,
  customerDataApiMapper,
  customerDataParamsMapper,
  customerOrderFilterMapper,
  customersApiMapper
} from "./mappers";
import { request, sendFile } from "../request";
import { useCallback, useState } from "react";

import { ApiOrder } from "../order/interface.api";
import { OrderFilterParams } from "../order/interface";
import { mapApiOrder } from "../order/mappers";

const hook = ({ serverPath, setError, setLoading, setSuccess, token }: ApiHooksProps): CustomerApi => {
  const [customerData, setCustomerData] = useState<CustomerData | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerOrders, setCustomerOrders] = useState<Order[]>([]);
  const [logoUrl, setLogoUrl] = useState<string>('');
  const [notes] = useState<OrderNote[]>([]);

  const addNote = useCallback(async (branchId: number, note: string) => {
    const path = `${serverPath}/notes/create`;
    const body = {
      Content: note,
      CustomerId: branchId
    }

    request(path, { body, token });
  }, [serverPath, token]);

  const createCustomer = useCallback(async (data: CustomerData) => {
    let success = true;
    const path = `${serverPath}/customers/customer`;
    const body = customerDataParamsMapper(data);

    setLoading(true);
    try {
      await request<ApiCustomerDataParams, ApiCustomerData>(path, { body, token });
      setSuccess('New customer created!');

      // Force the customers page to fetch a new list
      setCustomers([]);
    } catch (error: unknown) {
      success = false;
      
      if (typeof error === 'string') {
        setError(error);
      }
      else if (error instanceof Error) {
        setError(error.message || '');
      }
    }
    setLoading(false);

    return success;
  }, [serverPath, token, customers]);

  const editContact = useCallback(async (data: ApiContact) => {
    const path = `${serverPath}/customers/${data.CustomerId}/updateBranchContact/${data.BranchId}/${data.ContactId}`;
    const body = data;

    setLoading(true);
    await request<ApiContact, never>(path, { body, token });
    setLoading(false);
  }, [serverPath, token]);

  const getAddressList = useCallback(async () => {
    setLoading(true);
    const path = `${serverPath}/customers/address-list/`;
    const res = await request<never, ApiFileDownloadRequestID>(path, { token });
    const dlPath = `${serverPath}/customers/address-list-get/${res.FileDownloadRequestID}`;
    setLoading(false);
    window.open(dlPath, '_blank');
  }, [serverPath, token]);

  const getBranches = useCallback(async (customerId: string) => {
    const path = `${serverPath}/customers/${customerId}/get-branches/`;
    
    return request<never, ApiBranch[]>(path, { token });
  }, [serverPath, token]);

  const getData = useCallback(async (branchId: number | null, id?: string) => {
    const path = `${serverPath}/customers/customer/getData/`;
    const body: ApiGetDataParam = {
      BranchId: branchId,
      Id: id || null,
    };

    setLoading(true);
    try {
      const res = await request<ApiGetDataParam, ApiCustomerData>(path, { body, token });
      const newCustomer = customerDataApiMapper(res);
      setCustomerData(newCustomer);
    } catch (error: unknown) {
      if (typeof error === 'string') {
        setError(error);
      }
      else if (error instanceof Error) {
        setError(error.message || '');
      }
    }
    setLoading(false);
  }, [serverPath, token]);

  const getList = useCallback(async (data?: GetListParams) => {
    setLoading(true);
    const path = `${serverPath}/customers/list`;
    const body: ApiGetListParams = {
      AccountName: data?.accountName || '',
      Address: data?.address || '',
      Code: data?.code || '',
      EmailAddress: data?.email || '',
      IsActive: data?.isActive || null,
      Name: data?.name || '',
      page: data?.page || 1
    }

    try {
      const res = await request<ApiGetListParams, ApiCustomer[]>(path, { body, token });
      setCustomers(customersApiMapper(res));
    } catch (error: unknown) {
      if (typeof error === 'string') {
        setError(error);
      }
      else if (error instanceof Error) {
        setError(error.message || '');
      }
    }
    setLoading(false);
  }, [serverPath, token]);

  const getLogo = useCallback(async (id?: string, shouldReturnDefault?: boolean) => {
    const path = `${serverPath}/customers/get-logo/`;
    const body = {
      Id: id
    }
    const res = await request<{ Id?: string }, string[]>(path, { body, token });

    const fetchedLogoUrl = shouldReturnDefault ? "https://photoplanet1.s3.eu-west-1.amazonaws.com/PhotoplanetExamples/redcross.png" :  res.length > 0 ? res[0] : '';

    //const fetchedLogoUrl = "https://photoplanet1.s3.eu-west-1.amazonaws.com/PhotoplanetExamples/redcross.png";

    setLogoUrl(fetchedLogoUrl);
  }, [serverPath, token]);

  const getNotes = useCallback(async (customerId: string) => {
    const path = `${serverPath}/customers/${customerId}/notes`;

    const res = await request<never, ApiCustomerDataNote[]>(path, { method: 'GET', token });

    return customerApiDataNotesMapper(res);
  }, [serverPath, token]);

  const getOrderList = useCallback(async (data: OrderFilterParams) => {
    const path = `${serverPath}/order/branch-orders/`;

    setLoading(true);
    const body = customerOrderFilterMapper(data)
    const res = await request<ApiCustomerOrderFilter, ApiOrder[]>(path, { body, method: 'POST', token });
    setLoading(false);

    return setCustomerOrders(mapApiOrder(res));
  }, [serverPath, token]);

  const invite = useCallback(async (id: string) => {
    const path = `${serverPath}/customers/invite/${id}`;

    setLoading(true);
    try {
      await request(path, { token });
      setSuccess('Customer invited!')
    } catch (error: unknown) {
      if (typeof error === 'string') {
        setError(error);
      }
      else if (error instanceof Error) {
        setError(error.message || '');
      }
    }
    setLoading(false);
    
    return;
  }, [serverPath, token]);

  const newContact = useCallback(async (data: ApiContact) => {
    setLoading(true);
    const path = `${serverPath}/customers/${data.CustomerId}/createBranchContact/${data.BranchId}`;
    const body = data;
    try {
      await request(path, { body, token });
      setSuccess('Contact created!');
    } catch (error: unknown) {
      if (typeof error === 'string') {
        setError(error);
      }
      else if (error instanceof Error) {
        setError(error.message || '');
      }
    }
    setLoading(false);
  }, [serverPath, token]);

  const removeContact = useCallback(async (customerId: string, branchId:number, contactId: number) => {
    const path = `${serverPath}/customers/${customerId}/deleteBranchContact/${branchId}/${contactId}`;
    
    setLoading(true);
    await request(path, { method: 'DELETE', token });
    setLoading(false);
    setSuccess('Contact removed!');
  }, [serverPath, token]);

  const updateCustomer = useCallback(async (data: CustomerData) => {
    const path = `${serverPath}/customers/customer/`;
    const body = customerDataParamsMapper(data);

    setLoading(true);
    try {
      await request(path, { body, token });
      setSuccess('Customer updated!!')
    } catch (error: unknown) {
      if (typeof error === 'string') {
        setError(error);
      }
      else if (error instanceof Error) {
        setError(error.message || '');
      }
    }
    setLoading(false);

    return;
  }, [serverPath, token]);

  const uploadLogo = useCallback(async (customerId: string, data: Blob) => {
    const path = `${serverPath}/customers/upload-logo/${customerId}`;
    setLoading(true);
    try {
      await sendFile(path, data, token);
    } catch (error: unknown) {
      if (typeof error === 'string') {
        setError(error);
      }
      else if (error instanceof Error) {
        setError(error.message || '');
      }
    }
    setLoading(false);

  }, [serverPath, token]);

  const updateDetails = useCallback(async (data: ApiCustomerAddress, id: string, branchId: string) => {
    const path = `${serverPath}/customers/${id}/updateBranchDetails/${branchId}`;
    const body = data;
    setLoading(true);
    await request(path, { body, token });
    setLoading(false);
    setSuccess('Details updated!');
  }, [serverPath, token]);

  return {
    addNote,
    createCustomer,
    customerData,
    customerOrders,
    customers,
    editContact,
    getAddressList,
    getBranches,
    getData,
    getList,
    getLogo,
    getNotes,
    getOrderList,
    invite,
    logoUrl,
    newContact,
    notes,
    removeContact,
    updateCustomer,
    updateDetails,
    uploadLogo,
  }
}

export default hook;
